import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import * as XLSX from 'xlsx';

const skeletonStyles = `
  @keyframes shimmer {
    0% { background-position: -1000px 0; }
    100% { background-position: 1000px 0; }
  }
  .skeleton-box {
    display: inline-block;
    height: 1em;
    position: relative;
    overflow: hidden;
    background-color: #f0f0f0;
    background-image: linear-gradient(to right, #f0f0f0 0%, #e0e0e0 20%, #f0f0f0 40%, #f0f0f0 100%);
    background-repeat: no-repeat;
    background-size: 1000px 100%;
    animation: shimmer 2s infinite linear;
    border-radius: 4px;
    width: 100%;
  }
  .skeleton-circle {
    display: inline-block;
    width: 50px;
    height: 50px;
    border-radius: 10%;
    background-color: #f0f0f0;
    background-image: linear-gradient(to right, #f0f0f0 0%, #e0e0e0 20%, #f0f0f0 40%, #f0f0f0 100%);
    background-size: 1000px 100%;
    animation: shimmer 2s infinite linear;
  }
  .image-input-placeholder {
    background-image: url('assets/media/svg/files/blank-image.svg');
  }
  [data-bs-theme="dark"] .image-input-placeholder {
    background-image: url('assets/media/svg/files/blank-image-dark.svg');
  }
`;

const toProperCase = (text = '') => {
  if (!text) return '';
  const lowerCaseWords = new Set([
    'of', 'and', 'or', 'as', 'in', 'on', 'at', 'to', 'for', 'by', 'with', 'via', 'vs', 'etc', 'the', 'a', 'an'
  ]);
  return text
    .toLowerCase()
    .split(' ')
    .map((word, index, arr) => {
      if (index !== 0 && index !== arr.length - 1 && lowerCaseWords.has(word)) {
        return word;
      }
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
};

const EDO_LGAS = [
  'Akoko-Edo', 'Egor', 'Esan Central', 'Esan North-East', 'Esan South-East',
  'Esan West', 'Etsako Central', 'Etsako East', 'Etsako West', 'Igueben',
  'Ikpoba-Okha', 'Oredo', 'Orhionmwon', 'Ovia North-East', 'Ovia South-West',
  'Owan East', 'Owan West', 'Uhunmwonde'
];

const Agents = () => {
  const BASE_URL = 'https://adera-web-api.azurewebsites.net/v1';
  const ITEMS_PER_PAGE = 20;
  const TARGET_GOAL = 2000;

  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [agents, setAgents] = useState([]);
  const [totalAgentsCount, setTotalAgentsCount] = useState(0);
  const [globalAgentCount, setGlobalAgentCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [lgaFilter, setLgaFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [businessSort, setBusinessSort] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [imagePreview, setImagePreview] = useState('assets/media/svg/files/blank-image.svg');
  const fileInputRef = useRef(null);

  const [newAgentForm, setNewAgentForm] = useState({
    firstName: '',
    lastName: '',
    email: ''
  });

  const [selectedAgent, setSelectedAgent] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [agentClients, setAgentClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [clientPage, setClientPage] = useState(1);
  const [totalClients, setTotalClients] = useState(0);

  const getToken = () => localStorage.getItem('token');

  const formatDate = useCallback((dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch (e) { return 'N/A'; }
  }, []);

  useEffect(() => {
    const fetchGlobalCount = async () => {
      try {
        const token = getToken();
        const response = await fetch(`${BASE_URL}/admin/agent?page=1&count=1`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const result = await response.json();
        if (result.ok) {
          setGlobalAgentCount(result.data.totalItems || result.data.total || 0);
        }
      } catch (error) {
        console.error(error);
      }
    };
    fetchGlobalCount();
  }, []);

  const fetchAgents = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const token = getToken();
      const params = new URLSearchParams({
        page: page,
        count: ITEMS_PER_PAGE
      });

      if (searchTerm) params.append('search', searchTerm);
      if (lgaFilter) params.append('assignedLga', lgaFilter);
      if (statusFilter === 'Suspended') params.append('deactivated', 'true');
      else if (statusFilter === 'Active') params.append('deactivated', 'false');

      // Sort Logic
      if (businessSort === 'most') {
        params.append('sort', 'clientTotal');
        params.append('order', 'desc');
      } else if (businessSort === 'least') {
        params.append('sort', 'clientTotal');
        params.append('order', 'asc');
      } else if (businessSort === 'lga_asc') {
        params.append('sort', 'assignedLga'); 
        params.append('order', 'asc');
      } else if (businessSort === 'lga_desc') {
        params.append('sort', 'assignedLga'); 
        params.append('order', 'desc');
      }

      const response = await fetch(`${BASE_URL}/admin/agent?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();

      if (result.ok) {
        let fetchedData = result.data.pageData || [];
        const totalItems = result.data.totalItems || result.data.total || fetchedData.length;

        const mappedAgents = fetchedData.map((agent) => ({
          id: agent.id,
          displayId: agent.code || 'N/A',
          name: toProperCase(`${agent.firstName || ''} ${agent.lastName || ''}`),
          image: agent.avatarUrl || 'assets/media/avatars/blank.png',
          phone: agent.phoneNumber,
          email: agent.email?.toLowerCase() || '',
          lga: toProperCase(agent.assignedLga) || 'Unassigned',
          status: agent.deactivated ? 'Suspended' : 'Active',
          businessCount: agent.client?.total !== undefined ? agent.client.total : 0,
          raw: agent,
          createdAt: agent.createdAt
        }));

        const filteredAgents = mappedAgents.filter(agent => agent.email !== 'e.oloruntoba@edsaa.com.ng');

        setAgents(filteredAgents);
        setTotalAgentsCount(totalItems);
        setTotalPages(Math.ceil(totalItems / ITEMS_PER_PAGE));
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, lgaFilter, statusFilter, businessSort]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchAgents(currentPage);
    }, 400);
    return () => clearTimeout(timer);
  }, [currentPage, searchTerm, lgaFilter, statusFilter, businessSort, fetchAgents]);

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const exportAgents = async () => {
    setExporting(true);
    try {
      const token = getToken();
      const response = await fetch(`${BASE_URL}/admin/agent?page=1&count=10000`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();

      if (result.ok) {
        const allAgents = result.data.pageData || [];
        const filteredForExport = allAgents.filter(agent => agent.email?.toLowerCase() !== 'e.oloruntoba@edsaa.com.ng');

        if (filteredForExport.length === 0) {
          alert("No agents found to export");
          setExporting(false);
          return;
        }
        const dataToExport = filteredForExport.map(agent => ({
          "Agent Code": agent.code || 'N/A',
          "First Name": toProperCase(agent.firstName),
          "Last Name": toProperCase(agent.lastName),
          "Phone": agent.phoneNumber,
          "Email": agent.email?.toLowerCase() || '',
          "LGA": toProperCase(agent.assignedLga) || 'N/A',
          "Onboarded Businesses": agent.client?.total || 0,
          "Status": agent.deactivated ? 'Suspended' : 'Active',
          "Date Created": formatDate(agent.createdAt)
        }));
        const worksheet = XLSX.utils.json_to_sheet(dataToExport);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "All_Agents");
        XLSX.writeFile(workbook, `All_Agents_List_${new Date().toISOString().split('T')[0]}.xlsx`);
      } else {
        alert("Failed to fetch data for export");
      }
    } catch (error) {
      console.error("Export error:", error);
      alert("An error occurred during export");
    } finally {
      setExporting(false);
    }
  };

  const fetchAgentClients = async (agentId, page) => {
    setClientsLoading(true);
    try {
      const token = getToken();
      const response = await fetch(`${BASE_URL}/admin/agent/${agentId}/client?page=${page}&count=7`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();
      if (result.ok) {
        const formattedClients = (result.data.pageData || []).map(client => {
          const isCompany = client.type === 'Company';
          const clientName = isCompany ? client.company?.name : `${client.individual?.firstName || ''} ${client.individual?.lastName || ''}`;
          const clientEmail = isCompany ? client.company?.email : client.individual?.email;
          const clientPhone = isCompany ? client.company?.phoneNumber : client.individual?.phoneNumber;

          return {
            ...client,
            formattedName: toProperCase(clientName || ''),
            formattedEmail: clientEmail?.toLowerCase() || '',
            formattedPhone: clientPhone,
            isCompany,
            originalName: clientName
          };
        });

        setAgentClients(formattedClients);
        setTotalClients(result.data.totalItems || result.data.total || 0);
      }
    } catch (error) { console.error(error); }
    finally { setClientsLoading(false); }
  };

  const openProfileModal = async (agentBasicInfo) => {
    setSelectedAgent(agentBasicInfo);
    setShowProfileModal(true);
    setProfileLoading(true);
    setClientPage(1);
    setAgentClients([]);
    setTotalClients(0);
    const token = getToken();
    try {
      const detailRes = await fetch(`${BASE_URL}/admin/agent/${agentBasicInfo.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const detailData = await detailRes.json();
      if (detailData.ok) {
        const fullDetails = {
          ...agentBasicInfo,
          ...detailData.data,
          name: toProperCase(`${detailData.data.firstName} ${detailData.data.lastName}`),
          image: detailData.data.avatarUrl || agentBasicInfo.image,
          email: detailData.data.email?.toLowerCase() || ''
        };
        setSelectedAgent(fullDetails);
      }
      await fetchAgentClients(agentBasicInfo.id, 1);
    } catch (error) { console.error(error); }
    finally { setProfileLoading(false); }
  };

  const handleClientPageChange = (newPage) => {
    setClientPage(newPage);
    if (selectedAgent) fetchAgentClients(selectedAgent.id, newPage);
  };

  const handleAgentAction = async (actionType) => {
    if (!selectedAgent) return;
    const token = getToken();
    let url = '', method = 'POST';
    if (actionType === 'deactivate') {
      url = `${BASE_URL}/admin/agent/${selectedAgent.id}/deactivate`;
      method = 'DELETE';
    } else if (actionType === 'activate') {
      url = `${BASE_URL}/admin/agent/${selectedAgent.id}/activate`;
      method = 'POST';
    } else if (actionType === 'password') {
      url = `${BASE_URL}/admin/agent/${selectedAgent.id}/set-password`;
      method = 'POST';
    }
    try {
      const response = await fetch(url, { method, headers: { 'Authorization': `Bearer ${token}` } });
      const result = await response.json();
      if (result.ok) {
        alert(`Action ${actionType} successful`);
        fetchAgents(currentPage);
        if (actionType !== 'password') setShowProfileModal(false);
      } else {
        alert(result.error?.message || 'Action failed');
      }
    } catch (error) { alert('Action failed'); }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setNewAgentForm(prev => {
      const updated = { ...prev, [name]: value };
      if (name === 'firstName' || name === 'lastName') {
        const fName = name === 'firstName' ? value : prev.firstName;
        const lName = name === 'lastName' ? value : prev.lastName;
        if (fName && lName) {
          updated.email = `${fName.charAt(0)}.${lName}@edsaa.com.ng`.toLowerCase();
        } else {
          updated.email = '';
        }
      }
      return updated;
    });
  };

  const handleAddAgent = async (e) => {
    e.preventDefault();
    const form = e.target;
    const formData = new FormData(form);
    if (!formData.get('email')) {
      formData.append('email', newAgentForm.email);
    }
    try {
      const response = await fetch(`${BASE_URL}/admin/agent`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` },
        body: formData
      });
      const result = await response.json();
      if (result.ok) {
        alert('Agent added successfully!');
        setShowAddModal(false);
        form.reset();
        setNewAgentForm({ firstName: '', lastName: '', email: '' });
        setImagePreview('assets/media/svg/files/blank-image.svg');
        fetchAgents(currentPage);
      } else {
        alert(result.error?.message || 'Failed to create agent');
      }
    } catch (error) { alert('Network error occurred'); }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  const renderSkeletons = () => {
    return Array.from({ length: 10 }).map((_, idx) => (
      <tr key={`skel-${idx}`}>
        <td>
          <div className="d-flex align-items-center">
            <div className="skeleton-circle me-3"></div>
            <div className="d-flex flex-column w-100">
              <div className="skeleton-box mb-1" style={{ width: '60%' }}></div>
              <div className="skeleton-box" style={{ width: '40%' }}></div>
            </div>
          </div>
        </td>
        <td><div className="skeleton-box" style={{ width: '80%' }}></div></td>
        <td><div className="skeleton-box" style={{ width: '90%' }}></div></td>
        <td><div className="skeleton-box" style={{ width: '70%' }}></div></td>
        <td><div className="skeleton-box" style={{ width: '40px', height: '25px', borderRadius: '4px' }}></div></td>
        <td><div className="skeleton-box" style={{ width: '60px', height: '25px', borderRadius: '4px' }}></div></td>
        <td className="text-end">
          <div className="d-flex justify-content-end">
            <div className="skeleton-box" style={{ width: '80px', height: '30px' }}></div>
          </div>
        </td>
      </tr>
    ));
  };

  return (
    <>
      <style>{skeletonStyles}</style>
      <div className="d-flex flex-column flex-root app-root" id="kt_app_root">
        <div className="app-page flex-column flex-column-fluid" id="kt_app_page">
          <div id="kt_app_header" className="app-header">
            <div className="app-container container-fluid d-flex align-items-stretch justify-content-between">
              <div className="app-header-logo d-flex align-items-center me-lg-20 gap-2">
                <Link to="/">
                  <img alt="Logo" src="assets/aderalogo.png" className="h-60px" />
                </Link>
              </div>
              <div className="d-flex align-items-stretch justify-content-between flex-lg-grow-1">
                <div className="d-flex align-items-stretch" id="kt_app_header_menu_wrapper">
                  <div className="app-header-menu app-header-mobile-drawer align-items-stretch">
                    <div className="menu menu-rounded menu-column menu-lg-row menu-active-bg menu-title-gray-600 fw-semibold fs-6 align-items-stretch my-5 px-2">
                      <div className="menu-item me-2"><Link to="/dashboarda" className="menu-link"><span className="menu-title">Home</span></Link></div>
                      <div className="menu-item me-2"><Link to="/ad-management" className="menu-link"><span className="menu-title">Advertisement</span></Link></div>
                      <div className="menu-item here show menu-here-bg me-2"><Link to="/agents" className="menu-link"><span className="menu-title">Agents</span></Link></div>
                      <div className="menu-item me-2"><Link to="/payments" className="menu-link"><span className="menu-title">Payments</span></Link></div>
                    </div>
                  </div>
                </div>
                <div className="app-navbar flex-shrink-0 d-flex align-items-center gap-3">
                  <div className="text-end d-none d-sm-flex flex-column justify-content-center me-3">
                    <span className="text-gray-500 fs-8 fw-bold">Hello</span>
                    <span className="text-gray-800 fs-7 fw-bold">EDSAA Admin</span>
                  </div>
                  <div className="cursor-pointer symbol symbol-circle symbol-40px">
                    <img src={"assets/edsal.png"} alt="User" />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="app-wrapper d-flex flex-column flex-column-fluid" id="kt_app_wrapper">
            <div className="app-container container-fluid d-flex flex-row-fluid">
              <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
                <div className="d-flex flex-column flex-column-fluid">
                  <div id="kt_app_content" className="app-content flex-column-fluid">
                    <div className="d-flex flex-stack flex-wrap flex-md-nowrap card-rounded shadow p-5 p-lg-10 mb-10" style={{ background: 'linear-gradient(90deg, #20AA3E 0%, #03A588 100%)' }}>
                      <div className="my-2 me-5">
                        <div className="fs-2x fw-bold text-white mb-2">
                          <span className="text-white text-decoration-underline"><b>{globalAgentCount}</b></span>{' '}
                          <span className="fw-normal">Total Agents</span>
                        </div>
                      </div>
                      <div className="d-flex gap-3 my-2">
                        <button className="btn btn-lg border-2 btn-light flex-shrink-0" onClick={() => setShowAddModal(true)}>
                          <i className="ki-outline ki-plus fs-2 me-1 text-dark"></i> Add New Agent
                        </button>
                      </div>
                    </div>
                    <div className="card">
                      <div className="card-header border-0 pt-6">
                        <div className="card-title">
                          <div className="d-flex align-items-center position-relative my-1">
                            <i className="ki-outline ki-magnifier fs-3 position-absolute ms-5"></i>
                            <input type="text" className="form-control w-400px ps-12" placeholder="Search Agents" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                          </div>
                        </div>
                        <div className="card-toolbar">
                          <div className="me-3">
                          <select 
                            className="form-select w-200px" 
                            value={businessSort} 
                            onChange={(e) => {
                              setBusinessSort(e.target.value);
                              setCurrentPage(1); // Resets page so you don't fetch an empty page
                            }}
                          >
                            <option value="">Sort Agents</option>
                            <option value="most">Most Business</option>
                            <option value="least">Least Business</option>
                            <option value="lga_asc">LGA (A - Z)</option>
                            <option value="lga_desc">LGA (Z - A)</option>
                          </select>
                        </div>
                        <div className="me-3">
                          <select 
                            className="form-select w-200px" 
                            value={lgaFilter} 
                            onChange={(e) => {
                              setLgaFilter(e.target.value);
                              setCurrentPage(1); 
                            }}
                          >
                            <option value="">Filter LGA</option>
                            {EDO_LGAS.map((lga) => (
                              <option key={lga} value={lga}>{lga}</option>
                            ))}
                          </select>
                        </div>
                        </div>
                      </div>
                      <div className="card-body pt-0">
                        <div className="table-responsive">
                          <table className="table align-middle table-row-dashed fs-6 gy-5">
                            <thead>
                              <tr className="text-start text-muted fw-bold fs-7 text-uppercase gs-0">
                                <th className="min-w-200px">Agent</th>
                                <th className="min-w-100px">Phone</th>
                                <th className="min-w-150px">Email</th>
                                <th className="min-w-100px">LGA</th>
                                <th className="min-w-100px">Businesses</th>
                                <th className="min-w-100px">Status</th>
                                <th className="text-end min-w-100px">Actions</th>
                              </tr>
                            </thead>
                            <tbody className="text-gray-600 fw-semibold">
                              {loading ? renderSkeletons() : (
                                <>
                                  {agents.length === 0 && (
                                    <tr><td colSpan="7" className="text-center">No agents found</td></tr>
                                  )}
                                  {agents.map((agent) => (
                                    <tr key={agent.id}>
                                      <td>
                                        <div className="d-flex align-items-center">
                                          <div className="symbol symbol-50px me-5">
                                            <img src={agent.image} alt={agent.name} className="object-fit-cover" loading="lazy" />
                                          </div>
                                          <div className="d-flex flex-column">
                                            <a href="#" className="text-gray-800 text-hover-primary mb-1" onClick={(e) => { e.preventDefault(); openProfileModal(agent); }}>
                                              {agent.name}
                                            </a>
                                            <span className="text-gray-500 fs-9">Code: {agent.displayId}</span>
                                          </div>
                                        </div>
                                      </td>
                                      <td>{agent.phone}</td>
                                      <td>{agent.email}</td>
                                      <td>{agent.lga}</td>
                                      <td>
                                        <span className="badge badge-light-primary fw-bold px-4 py-2">
                                          {agent.businessCount}
                                        </span>
                                      </td>
                                      <td>
                                        <span className={`badge ${agent.status === 'Active' ? 'badge-light-success' : 'badge-light-danger'}`}>
                                          {agent.status}
                                        </span>
                                      </td>
                                      <td className="text-end">
                                        <button className="btn btn-sm btn-light-primary btn-active-primary" onClick={() => openProfileModal(agent)}>
                                          View Profile
                                        </button>
                                      </td>
                                    </tr>
                                  ))}
                                </>
                              )}
                            </tbody>
                          </table>
                        </div>
                        {!loading && (
                          <div className="d-flex justify-content-between align-items-center pt-4">
                            <div className="text-muted fs-7">Page {currentPage} of {totalPages}</div>
                            <div>
                              <button className="btn btn-sm btn-light me-2" disabled={currentPage === 1} onClick={() => goToPage(currentPage - 1)}>Prev</button>
                              <button className="btn btn-sm btn-light" disabled={currentPage === totalPages} onClick={() => goToPage(currentPage + 1)}>Next</button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="app-footer py-10 mt-10 border-top">
                    <div className="text-center">
                      <span className="text-muted fw-semibold me-1">2026©</span>
                      <a href="https://edsaa.com.ng/" target="_blank" rel="noopener noreferrer" className="text-gray-800 text-hover-primary fw-bold">EDSAA - ADERA</a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showAddModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered mw-800px">
            <div className="modal-content rounded-3">
              <form onSubmit={handleAddAgent}>
                <div className="modal-header border-0 pb-0 justify-content-between">
                  <h2 className="fw-bold m-0">Add New Agent</h2>
                  <button type="button" className="btn btn-icon btn-sm btn-active-light-primary" onClick={() => setShowAddModal(false)}>
                    <i className="ki-outline ki-cross fs-2"></i>
                  </button>
                </div>
                <div className="modal-body scroll-y py-5 px-lg-10">
                  <div className="mb-7 text-center">
                    <div
                      className="image-input image-input-outline image-input-placeholder mb-3"
                      onClick={triggerFileInput}
                      style={{ cursor: 'pointer', display: 'inline-block', position: 'relative' }}
                    >
                      <div
                        className="image-input-wrapper w-125px h-125px shadow-sm"
                        style={{
                          backgroundImage: `url(${imagePreview})`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                          border: '3px solid #fff'
                        }}
                      ></div>
                      <label className="btn btn-icon btn-circle btn-active-color-primary w-25px h-25px bg-body shadow" style={{ position: 'absolute', bottom: '5px', right: '5px' }}><i className="ki-outline ki-pencil fs-7"></i></label>
                    </div>
                    <div className="form-text">Allowed file types: png, jpg, jpeg.</div>
                    <input type="file" name="avatar" className="d-none" accept=".png, .jpg, .jpeg" ref={fileInputRef} onChange={handleImageChange} />
                  </div>
                  <h4 className="fw-bold text-gray-800 mb-4">Agent Details</h4>
                  <div className="row g-5 mb-5">
                    <div className="col-md-4">
                      <label className="required fw-semibold fs-6 mb-2">Surname</label>
                      <input type="text" className="form-control form-control-solid" name="lastName" placeholder="e.g. Okafor" required value={newAgentForm.lastName} onChange={handleFormChange} />
                    </div>
                    <div className="col-md-4">
                      <label className="required fw-semibold fs-6 mb-2">First Name</label>
                      <input type="text" className="form-control form-control-solid" name="firstName" placeholder="e.g. Chinedu" required value={newAgentForm.firstName} onChange={handleFormChange} />
                    </div>
                    <div className="col-md-4">
                      <label className="fw-semibold fs-6 mb-2">Middle Name</label>
                      <input type="text" className="form-control form-control-solid" name="middleName" placeholder="Optional" />
                    </div>
                  </div>
                  <div className="row g-5 mb-5">
                    <div className="col-md-6">
                      <label className="required fw-semibold fs-6 mb-2">Sex</label>
                      <select className="form-select form-select-solid" name="gender" required>
                        <option value="">Select Gender</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="required fw-semibold fs-6 mb-2">Date of Birth</label>
                      <input type="date" className="form-control form-control-solid" name="birthday" required />
                    </div>
                  </div>
                  <div className="row g-5 mb-5">
                    <div className="col-md-6">
                      <label className="required fw-semibold fs-6 mb-2">Phone Number (WhatsApp)</label>
                      <input type="tel" className="form-control form-control-solid" name="phoneNumber" placeholder="e.g. 0803 123 4567" required />
                    </div>
                    <div className="col-md-6">
                      <label className="required fw-semibold fs-6 mb-2">Email</label>
                      <input type="email" className="form-control form-control-solid bg-secondary" name="email" placeholder="e.g. name@edsaa.com.ng" required value={newAgentForm.email} readOnly />
                    </div>
                  </div>
                  <div className="mb-5">
                    <label className="required fw-semibold fs-6 mb-2">Residential Address</label>
                    <input type="text" className="form-control form-control-solid" name="address" placeholder="House number, street, area, city" required />
                  </div>
                  <div className="row g-5 mb-8">
                    <div className="col-md-6">
                      <label className="required fw-semibold fs-6 mb-2">Assign to LGA</label>
                      <select className="form-select form-select-solid" name="assignedLga" required>
                        <option value="">Select LGA</option>
                        {EDO_LGAS.map((lga) => (
                          <option key={lga} value={lga}>{lga}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="required fw-semibold fs-6 mb-2">Password</label>
                      <input type="text" className="form-control form-control-solid" name="password" placeholder="Min 8 characters" value="Password" readOnly />
                    </div>
                  </div>
                  <div className="separator separator-dashed my-5"></div>
                  <h4 className="fw-bold text-gray-800 mb-4">Guarantor's Details</h4>
                  <div className="row g-5 mb-5">
                    <div className="col-md-6">
                      <label className="required fw-semibold fs-6 mb-2">Name</label>
                      <input type="text" className="form-control form-control-solid" name="guarantorName" placeholder="e.g. Emmanuel Johnson" required />
                    </div>
                    <div className="col-md-6">
                      <label className="required fw-semibold fs-6 mb-2">Phone Number</label>
                      <input type="tel" className="form-control form-control-solid" name="guarantorPhoneNumber" placeholder="e.g. 0803 987 6543" required />
                    </div>
                  </div>
                  <div className="mb-5">
                    <label className="required fw-semibold fs-6 mb-2">Guarantor's Address</label>
                    <input type="text" className="form-control form-control-solid" name="guarantorAddress" placeholder="Guarantor's full address" required />
                  </div>
                </div>
                <div className="modal-footer flex-center border-0 pt-0 pb-5">
                  <button type="button" className="btn btn-light me-3" onClick={() => setShowAddModal(false)}>Cancel</button>
                  <button type="submit" className="btn btn-primary">Save Agent</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {showProfileModal && selectedAgent && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered mw-900px">
            <div className="modal-content rounded shadow-lg">
              <div className="modal-header border-0 pb-0">
                <h3 className="fw-bold">Agent Profile</h3>
                <button type="button" className="btn btn-icon btn-sm btn-active-light-primary ms-2" onClick={() => setShowProfileModal(false)}>
                  <i className="ki-outline ki-cross fs-2"></i>
                </button>
              </div>
              <div className="modal-body py-5 px-lg-10">
                {profileLoading ? (
                  <div className="d-flex justify-content-center align-items-center py-10">
                    <div className="spinner-border text-primary me-3"></div>
                    <span>Loading details...</span>
                  </div>
                ) : (
                  <>
                    <div className="row mb-8">
                      <div className="col-md-4 text-center border-end">
                        <div className="symbol symbol-150px mb-4">
                          <img src={selectedAgent.image} alt="Agent" className="object-fit-cover" />
                        </div>
                        <h4 className="fw-bold mb-1">{selectedAgent.name}</h4>
                        <div className="text-muted fs-7 mb-2">{toProperCase(selectedAgent.address) || 'No Address'}</div>
                        <div className={`badge ${selectedAgent.status === 'Active' ? 'badge-light-success' : 'badge-light-danger'} fs-7 mb-3`}>
                          {selectedAgent.status}
                        </div>
                      </div>
                      <div className="col-md-8 ps-md-10">
                        <div className="row mb-5">
                          <div className="col-6 mb-4">
                            <div className="fw-semibold text-gray-600 fs-7">Phone</div>
                            <div className="fw-bold text-gray-800">{selectedAgent.phone}</div>
                          </div>
                          <div className="col-6 mb-4">
                            <div className="fw-semibold text-gray-600 fs-7">Email</div>
                            <div className="fw-bold text-gray-800">{selectedAgent.email}</div>
                          </div>
                          <div className="col-6 mb-4">
                            <div className="fw-semibold text-gray-600 fs-7">Date of Birth</div>
                            <div className="fw-bold text-gray-800">{formatDate(selectedAgent.birthday)}</div>
                          </div>
                          <div className="col-6 mb-4">
                            <div className="fw-semibold text-gray-600 fs-7">Guarantor</div>
                            <div className="fw-bold text-gray-800">{toProperCase(selectedAgent.guarantor?.name) || 'N/A'}</div>
                          </div>
                        </div>
                        <div className="mb-5">
                          <div className="d-flex justify-content-between mb-2">
                            <span className="fw-bold text-gray-700">Onboarded Business</span>
                            <span className="fw-bold text-gray-800">{totalClients} / {TARGET_GOAL}</span>
                          </div>
                          <div className="progress h-10px bg-light mb-1">
                            <div className="progress-bar bg-success" role="progressbar" style={{ width: `${Math.min((totalClients / TARGET_GOAL) * 100, 100)}%` }}></div>
                          </div>
                        </div>
                        <div className="d-flex gap-3">
                          <button className="btn btn-light-danger btn-sm flex-grow-1" onClick={() => handleAgentAction('deactivate')}>Deactivate</button>
                          <button className="btn btn-light-success btn-sm flex-grow-1" onClick={() => handleAgentAction('activate')}>Activate</button>
                          <button className="btn btn-primary btn-sm flex-grow-1" onClick={() => handleAgentAction('password')}>Reset Password</button>
                        </div>
                      </div>
                    </div>
                    <div className="card border">
                      <div className="card-header border-bottom-0 pt-4"><h5 className="card-title fw-bold">Onboarded Businesses ({totalClients})</h5></div>
                      <div className="card-body pt-0">
                        <div className="table-responsive">
                          <table className="table table-row-dashed align-middle gs-0 gy-4">
                            <thead>
                              <tr className="border-bottom-0 text-gray-600 fw-bold fs-7 text-uppercase">
                                <th className="min-w-200px">Business / Client</th>
                                <th className="min-w-100px">Type</th>
                                <th className="min-w-200px">Email</th>
                                <th className="min-w-150px">Phone</th>
                              </tr>
                            </thead>
                            <tbody>
                              {clientsLoading ? (
                                <tr><td colSpan="4" className="text-center"><div className="spinner-border spinner-border-sm text-primary"></div></td></tr>
                              ) : agentClients.length > 0 ? (
                                agentClients.map(client => (
                                  <tr key={client.id}>
                                    <td>
                                      <div className="d-flex align-items-center">
                                        <div className="symbol symbol-35px me-3">
                                          <div className="symbol-label fs-5 bg-light-primary text-primary fw-bold">
                                            {client.formattedName ? client.formattedName.charAt(0) : '-'}
                                          </div>
                                        </div>
                                        <span className="text-gray-800 fw-bold text-hover-primary mb-1 fs-6">{client.formattedName}</span>
                                      </div>
                                    </td>
                                    <td><span className="text-gray-600 fw-semibold d-block fs-7">{client.type || 'Individual'}</span></td>
                                    <td><span className="text-gray-600 fw-semibold d-block fs-7">{client.formattedEmail || 'N/A'}</span></td>
                                    <td><span className="text-gray-600 fw-semibold d-block fs-7">{client.formattedPhone || 'N/A'}</span></td>
                                  </tr>
                                ))
                              ) : (
                                <tr><td colSpan="4" className="text-center text-muted">No businesses found</td></tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                        {totalClients > 10 && (
                          <div className="d-flex justify-content-end pt-2">
                            <button className="btn btn-sm btn-light me-2" disabled={clientPage === 1} onClick={() => handleClientPageChange(clientPage - 1)}>Prev</button>
                            <button className="btn btn-sm btn-light" disabled={clientPage * 10 >= totalClients} onClick={() => handleClientPageChange(clientPage + 1)}>Next</button>
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Agents;